import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_URLS, API_CONFIG } from '../../config/api';
import { Search, Image as ImageIcon, Video, X, Check, Upload, Trash2, Loader2 } from 'lucide-react';

const MediaSelector = ({ 
  value, 
  onChange, 
  onSelect, // Backward compatibility için onSelect desteği
  label = "Görsel Seç", 
  placeholder = "Medya kütüphanesinden seçin...",
  className = ""
}) => {
  // onChange veya onSelect'ten biri kullanılabilir
  const handleChange = onChange || onSelect || (() => {});
  const [isOpen, setIsOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all'); // 'all', 'image', 'video'
  const fileInputRef = useRef(null);
  const BASE_URL = API_CONFIG.BASE_URL;

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
    }
  }, [isOpen]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URLS.ADMIN.MEDIA, { headers: { Accept: 'application/json' } });
      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();

      // Debug: log non-JSON responses to help diagnose server errors
      if (!contentType.includes('application/json')) {
        console.warn('Media API non-JSON response:', {
          status: response.status,
          contentType,
          bodyPreview: text.slice(0, 300)
        });
      }

      if (!response.ok) {
        console.error('Media API error status:', response.status, text.slice(0, 300));
        setMediaFiles([]);
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Error parsing media JSON:', e, 'Raw:', text.slice(0, 300));
        setMediaFiles([]);
        return;
      }

      // Support both { data: [...] } and [...] shapes
      const files = Array.isArray(data) ? data : (data?.data || []);
      setMediaFiles(files);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = mediaFiles.filter(file => {
    const matchesSearch = (file?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const fileType = file?.type || '';
    const isImage = fileType.startsWith('image/');
    const isVideo = fileType.startsWith('video/');
    
    let matchesType = true;
    if (mediaTypeFilter === 'image') {
      matchesType = isImage;
    } else if (mediaTypeFilter === 'video') {
      matchesType = isVideo;
    } else {
      matchesType = isImage || isVideo;
    }
    
    return matchesSearch && matchesType;
  });

  const handleSelectFile = (file) => {
    // Eğer URL zaten tam bir URL ise (http/https ile başlıyorsa), olduğu gibi kullan
    // Değilse BASE_URL ekle
    const fullUrl = (file.url.startsWith('http://') || file.url.startsWith('https://'))
      ? file.url
      : `${BASE_URL}${file.url}`;
    handleChange(fullUrl);
    setIsOpen(false);
  };

  const handleClear = () => {
    handleChange('');
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiBase = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8000' 
        : `${window.location.protocol}//${window.location.host}`;

      const response = await fetch(`${apiBase}/backend/admin_api.php?action=uploadMedia`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        // Refresh media list
        await fetchMedia();
        // Optionally select the newly uploaded file
        if (data.data?.url) {
          handleSelectFile({
            url: data.data.url,
            name: data.data.name || data.data.original_name,
            type: data.data.type
          });
        }
      } else {
        alert('Yükleme hatası: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Dosya yüklenirken bir hata oluştu: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (file, event) => {
    event.stopPropagation();
    
    if (!confirm(`"${file.name}" dosyasını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    setDeleting(file.name);
    try {
      const apiBase = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8000' 
        : `${window.location.protocol}//${window.location.host}`;

      const fileName = file.url.split('/').pop();
      const response = await fetch(`${apiBase}/backend/admin_api.php?action=deleteMedia&filename=${encodeURIComponent(fileName)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        // Refresh media list
        await fetchMedia();
        // If deleted file was selected, clear selection
        const fileUrl = (file.url.startsWith('http://') || file.url.startsWith('https://'))
          ? file.url
          : `${BASE_URL}${file.url}`;
        if (value === fileUrl) {
          handleChange('');
        }
      } else {
        alert('Silme hatası: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Dosya silinirken bir hata oluştu: ' + error.message);
    } finally {
      setDeleting(null);
    }
  };

  const getFileName = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(true)}
          className="px-3"
          title="Medya Seç (Resim veya Video)"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
        {value && (
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            className="px-3 text-red-600 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Preview */}
      {value && (
        <div className="mt-2">
          {(() => {
            // Check if value is a video by extension or by checking mediaFiles
            const isVideoByExtension = value.match(/\.(mp4|webm|ogg|avi|mov|wmv|mkv|flv)$/i);
            const matchedFile = mediaFiles.find(f => {
              const fileUrl = (f.url.startsWith('http://') || f.url.startsWith('https://')) 
                ? f.url 
                : `${BASE_URL}${f.url}`;
              return fileUrl === value || value.includes(f.url.split('/').pop()) || f.url.includes(value.split('/').pop());
            });
            const isVideoByType = matchedFile && (matchedFile.type || '').startsWith('video/');
            
            if (isVideoByExtension || isVideoByType) {
              return (
                <video
                  src={value}
                  className="w-40 h-24 object-cover rounded border"
                  controls
                  muted
                  playsInline
                />
              );
            } else {
              return (
                <img
                  src={value}
                  alt="Preview"
                  className="w-20 h-20 object-cover rounded border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              );
            }
          })()}
        </div>
      )}

      {/* Media Selector Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Medya Kütüphanesi</h3>
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="px-3"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Search and Filter */}
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Medya dosyalarında ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUpload}
                  accept="image/*,video/*"
                  className="hidden"
                  id="media-upload-input"
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Yükleniyor...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      <span>Yükle</span>
                    </>
                  )}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={mediaTypeFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMediaTypeFilter('all')}
                  className="text-xs"
                >
                  Tümü
                </Button>
                <Button
                  variant={mediaTypeFilter === 'image' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMediaTypeFilter('image')}
                  className="text-xs"
                >
                  <ImageIcon className="h-3 w-3 mr-1" />
                  Resimler
                </Button>
                <Button
                  variant={mediaTypeFilter === 'video' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMediaTypeFilter('video')}
                  className="text-xs"
                >
                  <Video className="h-3 w-3 mr-1" />
                  Videolar
                </Button>
              </div>
            </div>

            {/* Media Grid */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-gray-500">Yükleniyor...</div>
                </div>
              ) : filteredFiles.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-gray-500">
                    {searchTerm ? 'Arama kriterlerinize uygun dosya bulunamadı' : 'Henüz yüklenmiş medya dosyası yok'}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredFiles.map((file) => {
                    const fileUrl = (file.url.startsWith('http://') || file.url.startsWith('https://'))
                      ? file.url
                      : `${BASE_URL}${file.url}`;
                    const isVideo = (file.type || '').startsWith('video/');
                    const isSelected = value === fileUrl;
                    
                    return (
                      <div
                        key={file.name}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-colors group ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectFile(file);
                        }}
                      >
                        <div className="aspect-square bg-gray-100 relative">
                          {isVideo ? (
                            <>
                              <video
                                src={fileUrl}
                                className="w-full h-full object-cover pointer-events-none"
                                muted
                                preload="metadata"
                                playsInline
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 pointer-events-none">
                                <Video className="h-8 w-8 text-white" />
                              </div>
                            </>
                          ) : (
                            <img
                              src={fileUrl}
                              alt={file.name}
                              className="w-full h-full object-cover pointer-events-none"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400">Görsel yüklenemedi</div>';
                              }}
                            />
                          )}
                          {/* Delete button overlay */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => handleDelete(file, e)}
                              disabled={deleting === file.name}
                              className="h-8 w-8 p-0"
                            >
                              {deleting === file.name ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-medium truncate" title={file.name}>
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(file.uploaded_at).toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaSelector;
